import { CheckCircle2 } from "lucide-react";
import { Heart, Sun, Flower, Squiggle, Smiley, Cloud } from "./Illustrations";

const principles = [
  {
    title: "Παιδοκεντρική Προσέγγιση",
    desc: "Κάθε παιδί είναι μοναδικό. Σχεδιάζουμε εξατομικευμένα προγράμματα με σεβασμό στον ρυθμό και τις ανάγκες του.",
    bg: "bg-brand-pinkSoft",
    accent: "#FBC4D0",
    rotate: "-rotate-1",
  },
  {
    title: "Διεπιστημονική Συνεργασία",
    desc: "Η ομάδα μας συνεργάζεται στενά, ώστε κάθε παρέμβαση να είναι ολοκληρωμένη και ουσιαστική.",
    bg: "bg-brand-yellowSoft",
    accent: "#FBE4A8",
    rotate: "rotate-1",
  },
  {
    title: "Ενεργή Συμμετοχή Γονέων",
    desc: "Η οικογένεια είναι σύμμαχος στη θεραπευτική διαδικασία. Σας ενημερώνουμε και σας στηρίζουμε σε κάθε βήμα.",
    bg: "bg-brand-secondarySoft",
    accent: "#B8D6BE",
    rotate: "rotate-1",
  },
  {
    title: "Ζεστό & Φιλόξενο Περιβάλλον",
    desc: "Χώροι σχεδιασμένοι ώστε τα παιδιά να νιώθουν άνετα, ασφαλή και να εκφράζονται ελεύθερα.",
    bg: "bg-brand-blueSoft",
    accent: "#B8D6E8",
    rotate: "-rotate-1",
  },
];

export default function Philosophy() {
  return (
    <section
      id="filosofia"
      data-testid="philosophy-section"
      className="relative py-24 md:py-32 bg-brand-secondarySoft overflow-hidden"
    >
      <Sun className="absolute top-16 right-1/4 animate-spin-slow opacity-80 hidden md:block" size={80} />
      <Cloud className="absolute top-12 left-12 hidden lg:block" size={90} />
      <Flower className="absolute bottom-20 right-10 hidden md:block animate-wiggle" size={70} color="#FBC4D0" />
      <Heart className="absolute bottom-32 left-16 animate-pulse-soft hidden lg:block" size={28} color="#F4A899" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 relative order-2 lg:order-1">
            <Smiley className="absolute -top-8 -left-4 animate-wiggle z-10" size={70} color="#FBE4A8" />
            <Heart className="absolute -bottom-6 -right-4 animate-pulse-soft z-10" size={50} color="#FBC4D0" />

            <div className="relative rounded-[2.5rem] overflow-hidden border-4 border-dashed border-brand-yellow bg-white shadow-[0_15px_0_-3px_#FBC4D0]">
              <img
                src="/images/philosophy.jpg"
                alt="Παιδί σε δημιουργική δραστηριότητα"
                className="w-full h-[480px] md:h-[580px] object-cover"
                loading="lazy"
                decoding="async"
                data-testid="philosophy-image"
              />
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white border-2 border-brand-pink mb-6 rotate-1">
              <Heart size={18} color="#F4A899" />
              <span className="font-body text-sm font-bold text-brand-text">
                Η Φιλοσοφία μας
              </span>
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-text leading-tight">
              Πιστεύουμε σε{" "}
              <span className="font-hand text-5xl sm:text-6xl lg:text-7xl text-brand-primary">
                κάθε παιδί
              </span>
            </h2>
            <Squiggle className="my-5" width={150} color="#F4A899" />
            <p className="font-body text-base sm:text-lg text-brand-textSoft leading-relaxed max-w-2xl">
              Το κέντρο μας ιδρύθηκε με όραμα να προσφέρει εξειδικευμένη
              φροντίδα και υποστήριξη σε παιδιά, σε ένα περιβάλλον γεμάτο
              αποδοχή, σεβασμό και ζεστασιά.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {principles.map((p, idx) => (
                <div
                  key={p.title}
                  data-testid={`principle-${idx}`}
                  className={`${p.bg} ${p.rotate} hover:rotate-0 transition-transform duration-500 rounded-2xl p-5 border-2 border-dashed border-brand-text/15`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <CheckCircle2 className="text-brand-text flex-shrink-0 mt-0.5" size={20} fill={p.accent} />
                    <h3 className="font-heading font-bold text-brand-text text-base leading-tight">
                      {p.title}
                    </h3>
                  </div>
                  <p className="font-body text-sm text-brand-textSoft leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
